
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About MACGOC', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      submenu: [
        { name: 'Technology & Digital Services', path: '/services/technology' },
        { name: 'Healthcare Operations', path: '/services/healthcare' },
        { name: 'Logistics & Transportation', path: '/services/logistics' },
        { name: 'Gasoline Retail & C-Stores', path: '/services/fuel-retail' },
        { name: 'Business & Capital Advisory', path: '/services/capital-advisory' },
      ]
    },
    { name: 'Industries', path: '/industries' },
    { name: 'Leadership', path: '/leadership' },
    { name: 'Insights', path: '/insights' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-black/85 backdrop-blur-sm'
      }`}
      style={{ borderBottom: '1px solid rgba(168, 144, 120, 0.2)' }}
    >
      <div className="max-w-[1600px] mx-auto px-8">
        <div className="flex items-center justify-between h-[90px]">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <h1 
              className="text-2xl font-semibold tracking-[0.3em] uppercase"
              style={{
                fontFamily: 'Cinzel, serif',
                background: 'linear-gradient(90deg, #7A6A5A 0%, #A89078 20%, #C4B8A8 40%, #D8CCC0 50%, #C4B8A8 60%, #A89078 80%, #7A6A5A 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              MACGOC
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-11">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.submenu ? (
                  <>
                    <button
                      className={`text-sm uppercase tracking-wide transition-colors duration-300 flex items-center gap-1 whitespace-nowrap ${
                        location.pathname.startsWith('/services')
                          ? 'text-copper-light'
                          : 'text-gray-300 hover:text-copper-light'
                      }`}
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      {link.name}
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </button>
                    {isServicesOpen && (
                      <div
                        className="absolute top-full left-0 mt-4 w-72 bg-charcoal border border-charcoal-light rounded-lg shadow-2xl py-3"
                        onMouseEnter={() => setIsServicesOpen(true)}
                        onMouseLeave={() => setIsServicesOpen(false)}
                      >
                        {link.submenu.map((sublink) => (
                          <Link
                            key={sublink.path}
                            to={sublink.path}
                            className="block px-6 py-3 text-sm text-gray-300 hover:text-copper-light hover:bg-charcoal-dark transition-colors duration-200"
                          >
                            {sublink.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`text-sm uppercase tracking-wide transition-colors duration-300 whitespace-nowrap ${
                      location.pathname === link.path
                        ? 'text-copper-light'
                        : 'text-gray-300 hover:text-copper-light'
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
                {location.pathname === link.path && !link.submenu && (
                  <div className="absolute -bottom-[45px] left-0 right-0 h-0.5 bg-copper-metallic"></div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/contact"
            className="hidden lg:flex items-center gap-2 text-white px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap border border-copper/50 hover:border-copper-light hover:bg-copper/10"
          >
            Request Consultation
            <i className="ri-arrow-right-line"></i>
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white text-2xl w-10 h-10 flex items-center justify-center"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-charcoal border-t border-charcoal-light">
          <div className="px-8 py-6 space-y-4">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.submenu ? (
                  <>
                    <Link
                      to={link.path}
                      className="block text-gray-300 hover:text-copper-light transition-colors py-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                    <div className="pl-4 space-y-2 mt-2">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.path}
                          to={sublink.path}
                          className="block text-sm text-gray-400 hover:text-copper-light transition-colors py-1"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {sublink.name}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className="block text-gray-300 hover:text-copper-light transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              className="block border border-copper/50 text-white text-center px-8 py-3 rounded-full font-semibold mt-6 whitespace-nowrap"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Request Consultation
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
